import {
  addDays,
  addMonths,
  epochDayOf,
  isLive,
  MINUTES_PER_DAY,
  monthEnd,
  monthStart,
  resolveSegments,
  sameDayInMonth,
  weekStartOf,
  type TimeBlockRecord,
} from '@wiztime/core';

/** Pure model of the month view (2026-10b §9): colour-band segments, legend and keyboard moves. */

/** Segments narrower than this many pixels get no separating gap (§9.4). */
export const MIN_GAP_SEGMENT_PX = 3;

/** A stretch of one project on a day's 24-hour band. */
export interface BandSegment {
  readonly projectId: string;
  /** Wall minutes (minutes after 00:00 of the personal date), within the day's axis. */
  readonly startMin: number;
  readonly endMin: number;
  /** Position and width on the band, in percent of its width. */
  readonly leftPct: number;
  readonly widthPct: number;
  /** True when it directly follows a segment of another project (a 1px gap is drawn). */
  readonly abutsOtherProject: boolean;
}

export interface MonthDay {
  readonly date: string;
  readonly segments: readonly BandSegment[];
  /** Distinct projects of the day, most minutes first. */
  readonly projectIds: readonly string[];
}

export interface LegendEntry {
  readonly projectId: string;
  readonly minutes: number;
}

export interface MonthModel {
  readonly days: ReadonlyMap<string, MonthDay>;
  /** Projects that appear in the month, most minutes first. */
  readonly legend: readonly LegendEntry[];
  readonly hasRecords: boolean;
}

/**
 * Colour-band data of a month from its blocks: one `resolveSegments` call for the whole month
 * (overlapping minutes belong to the counted block), then the segments are handed to the
 * personal date of their block. Touching segments of one project merge into one stretch.
 */
export function buildMonthModel(
  records: readonly TimeBlockRecord[],
  month: string,
  dayStartMin: number,
): MonthModel {
  const first = monthStart(month);
  const last = monthEnd(month);
  const dateOfBlock = new Map<string, string>();
  for (const record of records) {
    if (!isLive(record)) continue;
    if (record.data.personalDate >= first && record.data.personalDate <= last) {
      dateOfBlock.set(record.id, record.data.personalDate);
    }
  }
  const perDay = new Map<string, BandSegment[]>();
  const legendMinutes = new Map<string, number>();
  const dayMinutes = new Map<string, Map<string, number>>();

  for (const segment of resolveSegments(records, { from: first, to: last })) {
    const date = dateOfBlock.get(segment.blockId);
    if (date === undefined) continue;
    const midnight = epochDayOf(date) * MINUTES_PER_DAY;
    const startMin = Math.max(segment.startAbs - midnight, dayStartMin);
    const endMin = Math.min(segment.endAbs - midnight, dayStartMin + MINUTES_PER_DAY);
    if (endMin <= startMin) continue;

    const list = perDay.get(date) ?? [];
    perDay.set(date, list);
    const previous = list[list.length - 1];
    if (previous !== undefined && previous.projectId === segment.projectId && previous.endMin === startMin) {
      list[list.length - 1] = {
        ...previous,
        endMin,
        widthPct: ((endMin - previous.startMin) / MINUTES_PER_DAY) * 100,
      };
    } else {
      list.push(
        newSegment(
          segment.projectId,
          startMin,
          endMin,
          dayStartMin,
          previous !== undefined && previous.endMin === startMin && previous.projectId !== segment.projectId,
        ),
      );
    }
    const minutes = endMin - startMin;
    legendMinutes.set(segment.projectId, (legendMinutes.get(segment.projectId) ?? 0) + minutes);
    const perProject = dayMinutes.get(date) ?? new Map<string, number>();
    dayMinutes.set(date, perProject);
    perProject.set(segment.projectId, (perProject.get(segment.projectId) ?? 0) + minutes);
  }

  const days = new Map<string, MonthDay>();
  for (const [date, segments] of perDay) {
    const projects = [...(dayMinutes.get(date) ?? new Map<string, number>())];
    projects.sort((a, b) => b[1] - a[1]);
    days.set(date, { date, segments, projectIds: projects.map(([projectId]) => projectId) });
  }
  const legend = [...legendMinutes]
    .map(([projectId, minutes]) => ({ projectId, minutes }))
    .sort((a, b) => b.minutes - a.minutes);
  return { days, legend, hasRecords: days.size > 0 };
}

function newSegment(
  projectId: string,
  startMin: number,
  endMin: number,
  dayStartMin: number,
  abutsOtherProject: boolean,
): BandSegment {
  return {
    projectId,
    startMin,
    endMin,
    leftPct: ((startMin - dayStartMin) / MINUTES_PER_DAY) * 100,
    widthPct: ((endMin - startMin) / MINUTES_PER_DAY) * 100,
    abutsOtherProject,
  };
}

/** Whether a segment gets the 1px separating gap at a band of `bandWidthPx` (0 = not measured). */
export function drawsGap(segment: BandSegment, bandWidthPx: number): boolean {
  if (!segment.abutsOtherProject) return false;
  return bandWidthPx <= 0 || (segment.widthPct / 100) * bandWidthPx >= MIN_GAP_SEGMENT_PX;
}

/** Keys of the month grid's keyboard model (§9.6). */
export const GridKey = {
  Left: 'ArrowLeft',
  Right: 'ArrowRight',
  Up: 'ArrowUp',
  Down: 'ArrowDown',
  Home: 'Home',
  End: 'End',
  PageUp: 'PageUp',
  PageDown: 'PageDown',
} as const;

/**
 * Where a navigation key moves the focus from `date` (a date of `month`), or null for a key
 * that is not a move. The result can lie in an adjacent month (arrow keys cross the month edge);
 * Home / End stay inside the month (the row's Monday / Sunday clamped to the month).
 */
export function moveFocus(date: string, month: string, key: string, ctrl: boolean): string | null {
  const first = monthStart(month);
  const last = monthEnd(month);
  switch (key) {
    case GridKey.Left:
      return addDays(date, -1);
    case GridKey.Right:
      return addDays(date, 1);
    case GridKey.Up:
      return addDays(date, -7);
    case GridKey.Down:
      return addDays(date, 7);
    case GridKey.Home: {
      if (ctrl) return first;
      const monday = weekStartOf(date);
      return monday < first ? first : monday;
    }
    case GridKey.End: {
      if (ctrl) return last;
      const sunday = addDays(weekStartOf(date), 6);
      return sunday > last ? last : sunday;
    }
    case GridKey.PageUp:
      return sameDayInMonth(date, addMonths(month, -1));
    case GridKey.PageDown:
      return sameDayInMonth(date, addMonths(month, 1));
    default:
      return null;
  }
}

/** The date the roving focus starts on: today when the month holds it, else the 1st. */
export function initialFocusDate(month: string, today: string): string {
  return today >= monthStart(month) && today <= monthEnd(month) ? today : monthStart(month);
}

/** Date shown when the view leaves the month grid (D / W / view switch, §9.6). */
export function dateWhenLeavingMonth(month: string, today: string, focusedDate: string | null): string {
  if (focusedDate !== null && focusedDate >= monthStart(month) && focusedDate <= monthEnd(month)) {
    return focusedDate;
  }
  return initialFocusDate(month, today);
}
